import { persistDraftNow } from './drafts';

type CustomerOption = {
  id: string;
  kind: string;
  name: string;
  document: string;
  trade_name: string;
  email: string;
  phone: string;
  cep: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  ibge: string;
  uf: string;
  country: string;
  notes: string;
};

type SearchResponse = {
  items: CustomerOption[];
  next_cursor: string;
  has_more: boolean;
};

function optionNode(item: CustomerOption): HTMLLIElement {
  const option = document.createElement('li');
  option.className = 'combobox__option';
  option.id = `opt-tomador-${item.id}`;
  option.dataset.value = item.id;
  option.dataset.label = item.name;
  option.setAttribute('role', 'option');
  option.setAttribute('aria-selected', 'false');
  // Mesmo desenho do command de Serviço: documento em mono à esquerda, nome em seguida.
  const documentText = document.createElement('span');
  documentText.className = 'combobox__code tabular';
  documentText.textContent = item.document;
  const name = document.createElement('span');
  name.textContent = item.name;
  option.append(documentText, name);
  return option;
}

function emptyNode(): HTMLLIElement {
  const option = document.createElement('li');
  option.className = 'combobox__empty';
  option.id = 'opt-tomador-vazio';
  option.setAttribute('role', 'option');
  option.setAttribute('aria-disabled', 'true');
  option.setAttribute('aria-selected', 'false');
  option.textContent = 'Nenhum tomador com esse termo nesta empresa.';
  return option;
}

function newCustomerNode(): HTMLLIElement {
  const option = document.createElement('li');
  option.className = 'combobox__option combobox__option--action';
  option.id = 'opt-tomador-novo';
  option.dataset.value = 'novo';
  option.dataset.label = 'Cadastrar novo tomador';
  option.setAttribute('role', 'option');
  option.setAttribute('aria-selected', 'false');
  const svgNS = 'http://www.w3.org/2000/svg';
  const icon = document.createElementNS(svgNS, 'svg');
  icon.setAttribute('class', 'icon');
  icon.setAttribute('width', '20');
  icon.setAttribute('height', '20');
  icon.setAttribute('aria-hidden', 'true');
  icon.setAttribute('focusable', 'false');
  const use = document.createElementNS(svgNS, 'use');
  use.setAttribute('href', '#i-plus');
  icon.append(use);
  option.append(icon, document.createTextNode('Cadastrar novo tomador'));
  return option;
}

function moreCustomersNode(): HTMLLIElement {
  const option = document.createElement('li');
  option.className = 'combobox__option';
  option.id = 'opt-tomador-mais';
  option.dataset.value = 'mais';
  option.dataset.label = 'Carregar mais tomadores';
  option.setAttribute('role', 'option');
  option.setAttribute('aria-selected', 'false');
  option.textContent = 'Carregar mais tomadores';
  return option;
}

export function bindTakerCombobox(): void {
  const root = document.querySelector<HTMLElement>('[data-taker-combobox]');
  if (!root || root.dataset.bound === 'true') return;
  const input = root.querySelector<HTMLInputElement>('[role="combobox"]');
  const hidden = root.querySelector<HTMLInputElement>('[data-taker-id]');
  const list = root.querySelector<HTMLUListElement>('[role="listbox"]');
  const status = root.querySelector<HTMLElement>('[data-taker-status]');
  const editButton = root.querySelector<HTMLButtonElement>('[data-taker-edit]');
  const endpoint = root.dataset.searchUrl;
  if (!input || !hidden || !list || !endpoint) return;

  root.dataset.bound = 'true';
  let timer: number | undefined;
  let aborter: AbortController | undefined;
  let active = -1;
  let nextCursor = '';
  let lastQuery = input.value.trim();

  const options = (): HTMLLIElement[] => Array.from(list.querySelectorAll<HTMLLIElement>('[role="option"]:not([aria-disabled="true"])'));
  const announce = (message: string): void => {
    if (status) status.textContent = message;
  };
  const open = (): void => {
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };
  const close = (): void => {
    list.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    active = -1;
  };
  const activate = (index: number): void => {
    const current = options();
    if (current.length === 0) return;
	active = (index + current.length) % current.length;
	current.forEach((item, position) => item.setAttribute('aria-selected', position === active ? 'true' : 'false'));
	const selected = current[active];
	if (!selected) return;
	input.setAttribute('aria-activedescendant', selected.id);
    selected.scrollIntoView({ block: 'nearest' });
  };

  const issueForm = document.querySelector<HTMLFormElement>('#form-emitir');
  const quickForm = document.querySelector<HTMLFormElement>('#form-tomador-rapido');
  const dialog = document.querySelector<HTMLDialogElement>('#dlg-tomador');
  const dialogTitle = dialog?.querySelector<HTMLElement>('[data-taker-dialog-title]');
  const submitButton = dialog?.querySelector<HTMLButtonElement>('[data-taker-submit]');
  const customerFields = ['kind', 'document', 'name', 'trade_name', 'email', 'phone', 'cep', 'street', 'number', 'complement', 'district', 'city', 'ibge', 'uf', 'country', 'notes'] as const;
  const setFormValue = (name: string, value: string): void => {
    const field = quickForm?.elements.namedItem(name);
    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement) {
      field.value = value;
      // input aplica a máscara; change atualiza o rótulo do select próprio (hidden).
      if (field instanceof HTMLInputElement && field.dataset.mask) field.dispatchEvent(new Event('input'));
      field.dispatchEvent(new Event('change'));
    }
  };
  const openCustomerDialog = (customer?: CustomerOption): void => {
    if (!quickForm || !dialog) return;
    customerFields.forEach((field) => {
      const fallback = field === 'country' ? 'BR' : field === 'kind' ? 'pj' : '';
      setFormValue(field, customer?.[field] ?? fallback);
    });
    quickForm.action = customer ? `/emitir/tomador/${encodeURIComponent(customer.id)}` : '/emitir/tomador';
    if (dialogTitle) dialogTitle.textContent = customer ? 'Editar tomador' : 'Cadastrar tomador';
    if (submitButton) submitButton.textContent = customer ? 'Salvar alterações' : 'Salvar tomador';
    close();
    if (!dialog.open) dialog.showModal();
    dialog.querySelector<HTMLInputElement>('input:not([type="hidden"])')?.focus();
  };
  const preserveDraft = async (): Promise<boolean> => {
    if (!issueForm || !quickForm) return false;
    if (!(await persistDraftNow(issueForm))) {
      announce('O rascunho precisa ser salvo antes de cadastrar o tomador nesta emissão.');
      return false;
    }
    const currentDraft = issueForm.elements.namedItem('draft_id');
    const quickDraft = quickForm.elements.namedItem('draft_id');
    if (currentDraft instanceof HTMLInputElement && quickDraft instanceof HTMLInputElement) {
      quickDraft.value = currentDraft.value;
    }
    return true;
  };
  // Cadastro novo vai para a página completa; o rascunho salvo traz a nota de volta intacta.
  const openNewCustomer = async (): Promise<void> => {
    close();
    if (!issueForm || !(await persistDraftNow(issueForm))) {
      announce('O rascunho precisa ser salvo antes de cadastrar o tomador. Use “Salvar rascunho” e tente de novo.');
      return;
    }
    const draft = issueForm.elements.namedItem('draft_id');
    if (!(draft instanceof HTMLInputElement) || draft.value === '') return;
    window.location.assign(`/tomadores/novo?draft=${encodeURIComponent(draft.value)}`);
  };
  const editSelectedCustomer = async (): Promise<void> => {
    if (hidden.value === '' || !(await preserveDraft())) return;
    announce('Carregando o cadastro selecionado…');
    try {
      const response = await fetch(`/emitir/tomador/${encodeURIComponent(hidden.value)}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        announce('Não foi possível abrir este cadastro. Busque o tomador novamente.');
        return;
      }
      openCustomerDialog((await response.json()) as CustomerOption);
    } catch {
      announce('Não foi possível abrir este cadastro. O rascunho continua salvo.');
    }
  };
  const choose = (option: HTMLLIElement): void => {
    if (option.dataset.value === 'novo') {
      void openNewCustomer();
      return;
    }
    if (option.dataset.value === 'mais') {
      if (nextCursor !== '') void search(nextCursor, true);
      return;
    }
    hidden.value = option.dataset.value ?? '';
    input.value = option.dataset.label ?? option.textContent?.trim() ?? '';
    input.dispatchEvent(new Event('change', { bubbles: true }));
    announce(`Tomador ${input.value} selecionado.`);
    if (editButton) editButton.hidden = hidden.value === '';
    close();
  };
  const bindOptions = (): void => {
    options().forEach((option) => {
      option.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        choose(option);
      });
    });
  };
  const render = (items: CustomerOption[], append: boolean, cursor: string): void => {
    if (append) {
      list.querySelectorAll('[data-value="mais"], [data-value="novo"]').forEach((node) => node.remove());
      const known = new Set(options().map((option) => option.dataset.value));
      items.filter((item) => !known.has(item.id)).forEach((item) => list.append(optionNode(item)));
    } else {
      list.replaceChildren(...items.map(optionNode));
      if (items.length === 0) list.append(emptyNode());
    }
    nextCursor = cursor;
    if (nextCursor !== '') list.append(moreCustomersNode());
    list.append(newCustomerNode());
    bindOptions();
    active = -1;
    open();
    const found = options().filter((option) => option.dataset.value !== 'mais' && option.dataset.value !== 'novo').length;
    const continuation = nextCursor === '' ? '' : ' Há mais resultados.';
    announce(`${found === 1 ? '1 tomador encontrado' : `${found} tomadores encontrados`}.${continuation}`);
  };
  const search = async (cursor = '', append = false): Promise<void> => {
    aborter?.abort();
    aborter = new AbortController();
    const query = append ? lastQuery : input.value.trim();
    if (!append) {
      lastQuery = query;
      nextCursor = '';
    }
    announce(append ? 'Carregando mais tomadores…' : 'Buscando tomadores…');
    try {
      const target = new URL(endpoint, window.location.origin);
      target.searchParams.set('q', query);
      target.searchParams.set('limite', '10');
      if (cursor !== '') target.searchParams.set('cursor', cursor);
      const response = await fetch(target, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: aborter.signal,
      });
      if (!response.ok) {
        announce('Não consultamos os tomadores agora. O rascunho continua salvo.');
        close();
        return;
      }
      const page = (await response.json()) as SearchResponse;
      if (!append && input.value.trim() !== query) return;
      render(page.items, append, page.has_more ? page.next_cursor : '');
    } catch (error: unknown) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        announce('Não consultamos os tomadores agora. O rascunho continua salvo.');
        close();
      }
    }
  };
  const schedule = (): void => {
    if (timer !== undefined) window.clearTimeout(timer);
    timer = window.setTimeout(() => void search('', false), 120);
  };

  bindOptions();
  input.addEventListener('input', () => {
    hidden.value = '';
    if (editButton) editButton.hidden = true;
    schedule();
  });
  input.addEventListener('focus', () => {
    open();
    schedule();
  });
  input.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      open();
      activate(active + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      open();
      activate(active - 1);
    } else if (event.key === 'Enter' && active >= 0) {
      event.preventDefault();
      const option = options()[active];
      if (option) choose(option);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close();
    }
  });
  document.addEventListener('pointerdown', (event) => {
    if (event.target instanceof Node && !root.contains(event.target)) close();
  });
  editButton?.addEventListener('click', () => void editSelectedCustomer());
  // SSR validation returns the same dialog with preserved fields. Re-enter the
  // modal top layer without resetting its form, then focus the related field.
  if (dialog?.open) {
    dialog.removeAttribute('open');
    dialog.showModal();
    dialog.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }
  document.querySelector<HTMLDialogElement>('#dlg-tomador')?.addEventListener('close', () => input.focus());
}
